import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import { REALISATIONS } from "@/data/content";
import { useLanguage } from "@/i18n/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function resolveImage(url) {
  if (!url) return "";
  return url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
}

export default function Realisations() {
  const [items, setItems] = useState(REALISATIONS);
  const { t } = useLanguage();

  useEffect(() => {
    axios
      .get(`${API}/realisations`)
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length > 0) setItems(r.data);
      })
      .catch(() => {});
  }, []);

  const labelFor = (r) => {
    const tr = t.realisations.labels?.[r.code];
    return {
      title: tr?.title || r.title,
      place: tr?.place || r.place,
    };
  };

  return (
    <section
      id="realisations"
      data-testid="realisations-section"
      className="py-20 lg:py-32 bg-white border-t border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14"
        >
          <div>
            <div className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#E31E24] font-semibold mb-4">
              {t.realisations.overline}
            </div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-5xl font-bold tracking-tight text-slate-900 max-w-2xl">
              {t.realisations.title}
            </h2>
          </div>
          <p className="text-base sm:text-lg text-slate-600 max-w-md lg:text-right">
            {t.realisations.text}
          </p>
        </motion.div>

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          data-testid="realisations-grid"
        >
          {items.map((r, i) => {
            const label = labelFor(r);
            return (
              <motion.figure
                key={r.id}
                data-testid={`realisation-card-${r.id}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="group relative overflow-hidden border border-slate-200 bg-slate-950"
              >
                <img
                  src={resolveImage(r.image_url || r.image)}
                  alt={label.title}
                  loading="lazy"
                  className="w-full aspect-[4/3] object-cover grayscale-[45%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-transparent to-transparent" />
                <span className="absolute top-4 left-4 font-mono-tech text-[11px] tracking-[0.25em] text-white bg-slate-950/60 backdrop-blur-sm px-3 py-1.5 border border-white/15">
                  {r.code}
                </span>
                <figcaption className="absolute bottom-0 inset-x-0 p-5 flex items-end justify-between gap-3">
                  <div>
                    <div className="font-display font-semibold text-white">
                      {label.title}
                    </div>
                    {label.place && (
                      <div className="mt-1 inline-flex items-center gap-1.5 text-xs text-white/70">
                        <MapPin size={12} className="text-[#E31E24]" />
                        {label.place}
                      </div>
                    )}
                  </div>
                  <span className="w-2 h-2 bg-[#E31E24] rotate-45 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </figcaption>
              </motion.figure>
            );
          })}
        </div>
      </div>
    </section>
  );
}
